import React, { useEffect, useState, memo } from "react";
import { FormGroup, Label, Input, Button, Spinner } from "reactstrap";
import { useTranslation } from "react-i18next";
import {
  useDetailQuery,
  useCreateMutation,
  useUpdateMutation,
} from "helpers/http-client/hooks/useCrud";
import { API_ENDPOINTS } from "helpers/http-client/httpConstants";

interface SupplierFormProps {
  supplierId?: string;
  onSuccess: (supplier: any) => void;
}

export interface SupplierFormValues {
  name: string;
  contact?: string;
  address?: string;
  phone?: string;
  email?: string;
}

const SupplierForm: React.FC<SupplierFormProps> = ({ supplierId, onSuccess }) => {
  const { t } = useTranslation();
  const isEdit = Boolean(supplierId);
  const url = isEdit ? `${API_ENDPOINTS.SUPPLIERS}/${supplierId}` : "";

  const { data, isLoading } = useDetailQuery<SupplierFormValues>(url, supplierId);

  const [formValues, setFormValues] = useState<SupplierFormValues>({
    name: "",
    contact: "",
    address: "",
    phone: "",
    email: "",
  });

  const { mutate: createSupplier } = useCreateMutation(API_ENDPOINTS.SUPPLIERS);
  const { mutate: updateSupplier } = useUpdateMutation(API_ENDPOINTS.SUPPLIERS);

  useEffect(() => {
    if (data && isEdit) {
      setFormValues({
        name: data.name || "",
        contact: data.contact || "",
        address: data.address || "",
        phone: data.phone || "",
        email: data.email || "",
      });
    }
  }, [data, isEdit]);

  const handleChange = (field: keyof SupplierFormValues, value: string) => {
    setFormValues((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = () => {
    const payload: SupplierFormValues = {
      name: formValues.name.trim(),
      contact: formValues.contact?.trim() || undefined,
      address: formValues.address?.trim() || undefined,
      phone: formValues.phone?.trim() || undefined,
      email: formValues.email?.trim() || undefined,
    };

    if (isEdit && supplierId) {
      updateSupplier({ id: supplierId, data: payload }, { onSuccess });
    } else {
      createSupplier(payload, { onSuccess });
    }
  };

  if (isEdit && isLoading) {
    return <Spinner color="primary" />;
  }

  return (
    <>
      <FormGroup>
        <Label>{t("Name")}</Label>
        <Input
          value={formValues.name}
          placeholder={t("Enter supplier name")}
          onChange={(e) => handleChange("name", e.target.value)}
          required
        />
      </FormGroup>

      <FormGroup>
        <Label>{t("Contact")}</Label>
        <Input
          value={formValues.contact || ""}
          placeholder={t("Contact person or department")}
          onChange={(e) => handleChange("contact", e.target.value)}
        />
      </FormGroup>

      <FormGroup>
        <Label>{t("Address")}</Label>
        <Input
          value={formValues.address || ""}
          placeholder={t("Supplier address")}
          onChange={(e) => handleChange("address", e.target.value)}
        />
      </FormGroup>

      <FormGroup>
        <Label>{t("Phone")}</Label>
        <Input
          value={formValues.phone || ""}
          placeholder={t("Phone number")}
          onChange={(e) => handleChange("phone", e.target.value)}
        />
      </FormGroup>

      <FormGroup>
        <Label>{t("Email")}</Label>
        <Input
          type="email"
          value={formValues.email || ""}
          placeholder={t("Email address")}
          onChange={(e) => handleChange("email", e.target.value)}
        />
      </FormGroup>

      <div className="d-flex justify-content-end">
        <Button color="primary" onClick={handleSubmit}>
          {isEdit ? t("Update") : t("Save")}
        </Button>
      </div>
    </>
  );
};

export default memo(SupplierForm);
